/**
 * List export format registry
 *
 * Single source of truth for the list export formats offered by `ExportListModal`:
 * labels, file metadata, which options each format supports, and the exporter dispatch.
 * Mirrors `deckRegistry.ts`.
 */
import type { ScryfallCard } from '@/types/card';
import type { CardList, EnrichedListCard, ListPriority } from '@/types/lists';

import { exportSingleListBackup } from './mtgcraft';
import { formatCardLine } from './text';

// ============================================================================
// Types
// ============================================================================

export type ListExportFormat = 'mtgcraft' | 'text' | 'csv';

export interface ListExportOptionsState {
  includeSetInfo: boolean;
}

export interface ListExportContext {
  list: CardList;
  cards: EnrichedListCard[];
  options: ListExportOptionsState;
  /** Supplies set / collector number for the `includeSetInfo` option. */
  fullCardCache: Map<string, ScryfallCard>;
}

export interface ListExportFormatMeta {
  value: ListExportFormat;
  label: string;
  description: string;
  /** Format segment of the generated filename */
  fileLabel: string;
  extension: string;
  mimeType: string;
  /** Options rendered for this format; all others are hidden */
  supportedOptions: (keyof ListExportOptionsState)[];
  /** Option values applied when this format is selected */
  defaults: Partial<ListExportOptionsState>;
  run: (ctx: ListExportContext) => string;
}

export const LIST_EXPORT_BASE_OPTIONS: ListExportOptionsState = {
  includeSetInfo: false,
};

// ============================================================================
// Exporters
// ============================================================================

const PRIORITY_LABELS: Record<ListPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function exportAsPlainText({ cards, options, fullCardCache }: ListExportContext): string {
  return cards
    .map((card) => {
      const printing = fullCardCache.get(card.scryfall_id);
      // Without a cached printing there is no set/collector number to emit.
      const includeSetInfo = options.includeSetInfo && printing != null;
      return formatCardLine(
        {
          quantity: card.quantity,
          card: { name: card.name, set: printing?.set ?? '', collector_number: printing?.collector_number ?? '' },
        },
        includeSetInfo,
      );
    })
    .join('\n');
}

function escapeForCsv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function exportAsCsv({ cards, fullCardCache }: ListExportContext): string {
  const headers = [
    'Name',
    'Set',
    'Collector Number',
    'Quantity',
    'Owned',
    'Owned (Other Editions)',
    'Available',
    'Priority',
    'Notes',
    'Price Each',
    'Total Price',
  ];

  const rows = cards.map((card) => {
    const printing = fullCardCache.get(card.scryfall_id);
    return [
      escapeForCsv(card.name),
      printing?.set.toUpperCase() ?? '',
      printing?.collector_number ?? '',
      card.quantity.toString(),
      card.owned_quantity.toString(),
      card.owned_other_editions.toString(),
      card.available_quantity.toString(),
      PRIORITY_LABELS[card.priority],
      escapeForCsv(card.notes ?? ''),
      card.unit_price != null ? card.unit_price.toFixed(2) : '',
      card.total_price != null ? card.total_price.toFixed(2) : '',
    ];
  });

  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
}

// ============================================================================
// Registry
// ============================================================================

export const LIST_EXPORT_FORMATS: ListExportFormatMeta[] = [
  {
    value: 'mtgcraft',
    label: 'MtgCraft Backup',
    description: 'Full JSON backup with all metadata',
    fileLabel: 'MtgCraft',
    extension: 'json',
    mimeType: 'application/json',
    supportedOptions: [],
    defaults: {},
    run: ({ list, cards }) => exportSingleListBackup(list, cards),
  },
  {
    value: 'text',
    label: 'Card List',
    description: 'Plain "qty name" lines',
    fileLabel: 'Card List',
    extension: 'txt',
    mimeType: 'text/plain',
    supportedOptions: ['includeSetInfo'],
    defaults: {},
    run: exportAsPlainText,
  },
  {
    value: 'csv',
    label: 'CSV Spreadsheet',
    description: 'All columns for analysis',
    fileLabel: 'CSV',
    extension: 'csv',
    mimeType: 'text/csv',
    supportedOptions: [],
    defaults: {},
    run: exportAsCsv,
  },
];

// ============================================================================
// Dispatch
// ============================================================================

export function getListExportFormat(format: ListExportFormat): ListExportFormatMeta {
  const meta = LIST_EXPORT_FORMATS.find((f) => f.value === format);
  if (!meta) throw new Error(`Unknown list export format: ${format}`);
  return meta;
}

/** Option state for a format: base defaults overridden by the format's defaults */
export function getListExportDefaults(format: ListExportFormat): ListExportOptionsState {
  return { ...LIST_EXPORT_BASE_OPTIONS, ...getListExportFormat(format).defaults };
}

export function exportList(format: ListExportFormat, ctx: ListExportContext): string {
  return getListExportFormat(format).run(ctx);
}
